// El RECUADRO LIBRE del cuestionario convertido en AJUSTES que el vendedor acepta con un toque (PLAN-NOCHE §2b, criterio
// 2; nunca en silencio). Lee las notas con reglas (funciona sin llave de IA, en el servidor y en la pantalla) y propone:
//   "dos recibos / dos medidores"            → variante dos_medidores (cuestionario.dos_medidores)
//   "va a crecer / minisplits / auto eléctrico" → aumento_consumo (cambio "aumenta" + su cifra si la escribió)
//   "casa o negocio nuevo, sin recibos"      → consumo_proyectado (cliente nuevo + su consumo estimado)
//   "recibo nuevo, pocos meses"              → historial_incompleto (el cotizador completa los meses: informativa)
//   "ya tiene paneles"                       → ampliacion (con lo que se lea: paneles, kWp)
//   "va a consumir menos"                    → cambio "disminuye"
//   "está a nombre de …"                     → titular
//   "sobre el suelo / estructura elevada / carport" → tipo de estructura del cuestionario (Diego, 12-oct: va dentro del
//                                              precio del sistema, no como costo adicional del catálogo)
//   "techo de lámina / lejos / mufa / minisplit en el techo / grúa" → costos adicionales sugeridos (catálogo)
// Lo que no se entiende no se inventa: si falta una cifra, la sugerencia lo dice ("falta") y no la pone. Con la llave
// de Anthropic, "Pedirle a la IA" (corregirConIA) puede proponer más; esto es la base que siempre está.
import type { ContenidoCotizacion } from './dominio/cotizacion.ts';
import type { EdicionCampo } from './dominio/edicion.ts';
import type { Variante } from './dominio/basicos.ts';
import { ETIQUETA_ESTRUCTURA, aumentoConIAAplica, estructuraDe } from './dominio/cuestionario.ts';
import type { TipoEstructura } from './dominio/cuestionario.ts';

export type IdSugerencia = 'dos_medidores' | 'aumento_consumo' | 'consumo_proyectado' | 'historial_incompleto' | 'ampliacion' | 'reduce' | 'titular' | 'estructura' | `costo:${string}`;

export interface SugerenciaNotas {
  id: IdSugerencia;
  /** La variante de la familia de PDF que activa (null = no es variante: costo, titular, baja de consumo). */
  variante: Variante | null;
  titulo: string;
  /** Qué entendió y qué va a cambiar, en palabras del vendedor. */
  explicacion: string;
  /** El pedazo de las notas que lo dijo (para que el vendedor vea de dónde salió). */
  frase: string;
  /** Ediciones del borrador que la aplican (con "antes" del borrador de hoy). Vacío = informativa o falta un dato. */
  ediciones: EdicionCampo[];
  /** Costo adicional sugerido: id del catálogo de la tabla y concepto (la pantalla abre el formulario ya llenado). */
  costo: { catalogo_id: string; concepto: string } | null;
  /** Lo que falta para aplicarla completa (p. ej. "¿cuántos kWh al año va a consumir?"). null = nada. */
  falta: string | null;
  /** Ya está en el cuestionario (o el costo ya está agregado): se enseña como hecha, sin botón. */
  aplicada: boolean;
}

const normal = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** El pedazo de texto (en el original, con acentos) alrededor de lo que coincidió, cortado en la oración. */
function fraseDe(original: string, normalizado: string, m: RegExpExecArray): string {
  const ini = Math.max(0, normalizado.lastIndexOf('.', m.index) + 1);
  const finPunto = normalizado.indexOf('.', m.index + m[0].length);
  const fin = finPunto === -1 ? original.length : finPunto;
  return original.slice(ini, fin).trim().slice(0, 200);
}

/** El motivo CORTO (la cláusula que lo dijo, ≤ 40 letras, sin paréntesis ni punto final): la variante aumento_consumo
 *  del PDF lo imprime así ("porque tu consumo va a subir (va a comprar dos minisplits)"); una oración larga salía cortada. */
function motivoCorto(original: string, normalizado: string, m: RegExpExecArray): string {
  const frase = fraseDe(original, normalizado, m);
  const fn = normal(frase);
  const pedazos = frase.split(/\s*(?:[,;:]|\by\b|\be\b|\bpero\b)\s*/i).filter((x) => x.trim().length >= 3);
  const clave = normal(m[0]).trim();
  let elegido = pedazos.find((x) => normal(x).includes(clave)) ?? pedazos[0] ?? frase;
  if (!fn.includes(clave)) elegido = frase;
  elegido = elegido.replace(/[()]/g, '').replace(/[.\s]+$/, '').trim();
  if (elegido.length > 40) elegido = elegido.slice(0, 41).replace(/\s+\S*$/, '').trim();
  return elegido.length >= 3 ? elegido : 'consumo nuevo';
}

const numeroDeTexto = (s: string) => Number(s.replace(/,/g, ''));

/** kWh al año que escribió el vendedor ("9,000 kWh al año", "750 kWh al mes" → ×12). null si no hay cifra clara. */
function kwhAnualEn(n: string): number | null {
  const anual = /(\d[\d,]*(?:\.\d+)?)\s*kwh\s*(?:al|por|cada)?\s*(?:ano|anuales|anual)/.exec(n);
  if (anual) return numeroDeTexto(anual[1]!);
  const mensual = /(\d[\d,]*(?:\.\d+)?)\s*kwh\s*(?:al|por|cada)?\s*mes/.exec(n);
  if (mensual) return Math.round(numeroDeTexto(mensual[1]!) * 12);
  const bimestral = /(\d[\d,]*(?:\.\d+)?)\s*kwh\s*(?:al|por|cada)?\s*bimestre/.exec(n);
  if (bimestral) return Math.round(numeroDeTexto(bimestral[1]!) * 6);
  return null;
}

function kwhMesEn(n: string): number | null {
  const a = kwhAnualEn(n);
  return a === null ? null : Math.round(a / 12);
}

const reemplazo = (ruta: string, antes: unknown, despues: unknown, motivo: string): EdicionCampo => ({ op: 'reemplazar', ruta, antes, despues, motivo });

interface ReglaCosto {
  re: RegExp;
  catalogo_id: string;
  concepto: string;
  por: string;
}
const COSTOS: readonly ReglaCosto[] = [
  { re: /techo de lamina|lamina (galvanizada|delgada|vieja)|teja/, catalogo_id: 'anclaje-especial', concepto: 'Anclaje especial (losa, teja o lámina delicada)', por: 'el techo pide anclaje especial' },
  { re: /lejos de hermosillo|fuera de hermosillo|\b(obregon|navojoa|guaymas|san carlos|caborca|nogales|kino|penasco|puerto penasco)\b|carretera|rancho/, catalogo_id: 'viaticos', concepto: 'Viáticos fuera de Hermosillo', por: 'es fuera de Hermosillo' },
  { re: /\bmufa\b/, catalogo_id: 'mufa', concepto: 'Modificación de mufa (fuera de norma CFE)', por: 'la mufa se tiene que cambiar' },
  { re: /(mover|quitar|reubicar) (el |los |un |una )?(minisplit|minisplits|tinaco|antena|clima)|(minisplit|minisplits|tinaco|clima)s? en el techo/, catalogo_id: 'mover-equipos', concepto: 'Mover equipos del techo (minisplit, tinaco, antena)', por: 'hay equipos que mover del techo' },
  { re: /\bgrua\b/, catalogo_id: 'grua', concepto: 'Grúa (por maniobra)', por: 'hace falta grúa' },
];

const ESTRUCTURAS_EN_NOTAS: readonly { re: RegExp; tipo: Exclude<TipoEstructura, 'losa_lamina'>; por: string }[] = [
  { re: /\bcarport\b|cochera (techada|con paneles)|paneles (en|sobre) (la|el) (cochera|estacionamiento)/, tipo: 'carport', por: 'va en un carport' },
  { re: /(sobre|en) (el )?(suelo|piso|terreno)|estructura (de|en) (suelo|piso)|montaje en suelo/, tipo: 'suelo', por: 'va sobre el suelo' },
  { re: /estructura elevada|elevad[oa]s? (del|sobre el) techo|pergola|\bptr\b/, tipo: 'ptr', por: 'pide estructura elevada' },
];

/** Sugerencias de ajustes que salen del recuadro libre (cuestionario.notas_libres). En el orden en que se enseñan. */
export function sugerenciasDeNotas(c: Pick<ContenidoCotizacion, 'cuestionario' | 'lecturas' | 'costos_adicionales' | 'parametros'>): SugerenciaNotas[] {
  const q = c.cuestionario;
  const texto = (q.notas_libres ?? '').normalize('NFC');
  const n = normal(texto);
  // Las frases se cortan del original (con acentos) por posición: solo si quitar los acentos no movió nada.
  const original = texto.length === n.length ? texto : n;
  if (!n.trim()) return [];
  const out: SugerenciaNotas[] = [];
  const motivo = 'Lo dicen las notas del vendedor';

  // ── Dos medidores
  const dos = /\b(dos|2|otro|segundo)\s+(recibos|medidores|servicios|contratos|recibo|medidor)\b/.exec(n);
  if (dos && !/\bsolo (un|1) (recibo|medidor)/.test(n))
    out.push({
      id: 'dos_medidores',
      variante: 'dos_medidores',
      titulo: 'Son dos medidores',
      explicacion: 'Se suman los dos recibos mes por mes y la propuesta lo explica. Después sube los recibos del medidor B.',
      frase: fraseDe(original, n, dos),
      ediciones: q.dos_medidores || q.consumo.es_nuevo ? [] : [reemplazo('/cuestionario/dos_medidores', q.dos_medidores, true, motivo)],
      costo: null,
      falta: q.consumo.es_nuevo ? 'Un cliente nuevo no tiene recibos que sumar.' : null,
      aplicada: q.dos_medidores,
    });

  // ── Cliente nuevo (consumo proyectado)
  const nuevo = /\b(casa|negocio|local|nave|bodega|oficina|departamento) (nuev[oa]|recien construid[oa])\b|\bsin recibos?\b|\bno (tiene|trae|hay) recibos?\b|todavia no (tiene|le llega|hay) (luz|recibo)|aun no (tiene|le llega) (luz|recibo)/.exec(n);
  if (nuevo) {
    const kwhMes = kwhMesEn(n);
    const ediciones =
      q.consumo.es_nuevo || kwhMes === null
        ? []
        : [
            reemplazo('/cuestionario/consumo/es_nuevo', q.consumo.es_nuevo, true, motivo),
            reemplazo('/cuestionario/estimacion', q.estimacion, { metodo: 'kwh_mes', cargas: [], kwh_mes: kwhMes, fuente: `Notas del vendedor: «${fraseDe(original, n, nuevo).slice(0, 200)}»`.slice(0, 300) }, motivo),
            ...(q.dos_medidores ? [reemplazo('/cuestionario/dos_medidores', true, false, 'Un cliente nuevo no tiene recibos que sumar')] : []),
          ];
    out.push({
      id: 'consumo_proyectado',
      variante: 'consumo_proyectado',
      titulo: 'Cliente nuevo: se estima su consumo',
      explicacion: kwhMes !== null ? `Se cotiza con ${kwhMes.toLocaleString('es-MX')} kWh al mes estimados y la propuesta explica cómo se estimó.` : 'Se cotiza con su consumo estimado y la propuesta explica cómo se estimó.',
      frase: fraseDe(original, n, nuevo),
      ediciones,
      costo: null,
      falta: q.consumo.es_nuevo ? null : kwhMes === null ? 'Falta cuánto va a consumir (kWh al mes) o su cuadro de cargas: edita el cuestionario («No, es nuevo»).' : null,
      aplicada: q.consumo.es_nuevo,
    });
  }

  // ── Aumento de consumo
  const sube =
    /\b(va|van) a (comprar|meter)\b|\b(piensa|planea|quiere|quieren) comprar\b|\b(minisplits?|aires? acondicionados?|climas?|auto electrico|carro electrico|coche electrico|alberca|bomba de alberca|calentador electrico)\b|otro cuarto|cuarto nuevo|ampliar la casa|(va|van) a crecer|consumo (va a )?(subir|aumentar|crecer)|(subir|aumentar|crecer)(a|á)? (su|el) consumo/.exec(n);
  const baja = /(va|van) a (consumir|gastar) menos|consumo (va a )?bajar|bajar(a)? (su|el) consumo|(cierra|cerrara|va a cerrar) (una|la|el)/.exec(n);
  if (sube && !nuevo) {
    const futuro = kwhAnualEn(n);
    // El consumo futuro tiene que pasar del de hoy (lo exige la variante aumento_consumo); sin lectura no se puede comparar.
    const actual = (c.lecturas.A?.historial ?? []).slice(-12).reduce((s, p) => s + p.consumo_kwh, 0);
    const futuroValido = futuro !== null && (actual === 0 || futuro > actual) ? futuro : null;
    const detalle = motivoCorto(original, n, sube);
    const ediciones: EdicionCampo[] = [];
    if (q.consumo.cambio !== 'aumenta') ediciones.push(reemplazo('/cuestionario/consumo/cambio', q.consumo.cambio, 'aumenta', motivo));
    if (!q.consumo.cambio_detalle) ediciones.push(reemplazo('/cuestionario/consumo/cambio_detalle', q.consumo.cambio_detalle, detalle, motivo));
    // ADENDA z52-2: donde se ofrece, el aumento lo calcula la IA en el cuestionario («Calcular el aumento»), con los equipos nuevos.
    // Verificación del 28-oct: si el CLIENTE dijo cuánto («va a consumir 30,000 kWh al año»), esa cifra manda y se escribe como
    // siempre (consumo futuro: la Holgada queda recomendada); antes se perdía y el vendedor tenía que estimarla desde los equipos. Con
    // un aumento ya aceptado no se escribe (son excluyentes). Bloqueo del verificador (28-oct): solo donde el motor suma el
    // aumento (en GDBT, GDMTO y GDMTH no): ahí la sugerencia es la de producción (consumo futuro, la Holgada recomendada).
    const conIA =
      aumentoConIAAplica({ ...q, consumo: { ...q.consumo, cambio: 'aumenta' } }, { tarifaLeida: c.lecturas.A?.tarifa ?? null, negocioAPdbt: c.parametros.negocio_a_pdbt === true }) &&
      (futuroValido === null || !!q.consumo.aumento);
    if (conIA) {
      const au = q.consumo.aumento ?? null;
      out.push({
        id: 'aumento_consumo',
        variante: 'aumento_consumo',
        titulo: 'Su consumo va a subir',
        explicacion: au
          ? `Ya se sumó el aumento (+${Math.round(au.kwh_mes.reduce((a, b) => a + b, 0)).toLocaleString('es-MX')} kWh al año, ${au.motivo}): las opciones, el ahorro y el retorno lo cuentan.`
          : 'Se marca que su consumo va a subir. Calcula cuánto con «Calcular el aumento» (la IA lo estima con los equipos nuevos) en el cuestionario.',
        frase: fraseDe(original, n, sube),
        ediciones,
        costo: null,
        falta: null,
        aplicada: q.consumo.cambio === 'aumenta' && ediciones.length === 0,
      });
    } else {
    if (futuroValido !== null && q.consumo.consumo_futuro_kwh_anual === null) {
      ediciones.push(reemplazo('/cuestionario/consumo/consumo_futuro_kwh_anual', null, futuroValido, motivo));
      // un aumento con IA que quedó de antes (la tarifa resultó con demanda: el motor no lo suma) no va junto al consumo futuro
      if (q.consumo.aumento) ediciones.push(reemplazo('/cuestionario/consumo/aumento', q.consumo.aumento, null, motivo));
    }
    out.push({
      id: 'aumento_consumo',
      variante: 'aumento_consumo',
      titulo: 'Su consumo va a subir',
      explicacion:
        futuroValido !== null
          ? `Se cotiza para ${futuroValido.toLocaleString('es-MX')} kWh al año: la Holgada queda como recomendada y la propuesta explica el aumento.`
          : 'Se marca que su consumo va a subir. Para que la propuesta lo explique y recomiende la Holgada, escribe cuánto va a consumir.',
      frase: fraseDe(original, n, sube),
      ediciones,
      costo: null,
      falta: futuroValido === null && q.consumo.consumo_futuro_kwh_anual === null ? '¿Cuántos kWh al año va a consumir? (en el cuestionario: «¿Su consumo va a cambiar?»)' : null,
      aplicada: q.consumo.cambio === 'aumenta' && (q.consumo.consumo_futuro_kwh_anual !== null || futuroValido === null) && ediciones.length === 0,
    });
    }
  } else if (baja) {
    out.push({
      id: 'reduce',
      variante: null,
      titulo: 'Su consumo va a bajar',
      explicacion: 'Se marca que va a consumir menos: revisa que la opción recomendada no le sobre.',
      frase: fraseDe(original, n, baja),
      ediciones: q.consumo.cambio === 'disminuye' ? [] : [reemplazo('/cuestionario/consumo/cambio', q.consumo.cambio, 'disminuye', motivo), ...(q.consumo.consumo_futuro_kwh_anual !== null ? [reemplazo('/cuestionario/consumo/consumo_futuro_kwh_anual', q.consumo.consumo_futuro_kwh_anual, null, motivo)] : [])],
      costo: null,
      falta: null,
      aplicada: q.consumo.cambio === 'disminuye',
    });
  }

  // ── Historial incompleto (recibo nuevo, pocos meses)
  const pocos = /recibo nuevo|contrato nuevo|pocos meses|solo (tiene|trae|hay|nos dieron) (\d+|un|dos|tres|cuatro|cinco) (recibos|meses)|(se cambio|se mudo|lo contrato) hace (poco|\d+ meses)|apenas (tiene|lleva) (\d+ )?mes/.exec(n);
  if (pocos && !nuevo)
    out.push({
      id: 'historial_incompleto',
      variante: 'historial_incompleto',
      titulo: 'Pocos meses de historial',
      explicacion: 'No hay que cambiar nada: sube los recibos que haya y el cotizador completa los meses que faltan con casas o negocios parecidos; la propuesta lo dice. Con menos de 3 meses reales, mejor cotízalo como cliente nuevo.',
      frase: fraseDe(original, n, pocos),
      ediciones: [],
      costo: null,
      falta: null,
      aplicada: false,
    });

  // ── Ampliación (ya tiene paneles)
  const amplia = /ya tiene (paneles|placas|un sistema|sistema|celdas)|tiene (\d+ )?(paneles|placas) (instalad|desde|de hace|puestos)|ampliar (el sistema|los paneles|su sistema)|agregar (mas )?paneles|mas paneles|ampliacion/.exec(n);
  if (amplia) {
    const paneles = /(\d{1,4})\s*(paneles|placas)/.exec(n);
    const kw = /(\d+(?:\.\d+)?)\s*(kwp|kw)\b/.exec(n);
    const existente = {
      paneles: paneles ? Number(paneles[1]) : null,
      panel_w: null,
      kwp: kw ? Number(kw[1]) : null,
      inversor: null,
      notas: fraseDe(original, n, amplia).slice(0, 1000),
      inversores: null,
      anio_instalacion: null,
      instalo: null,
      conexion: null,
    };
    out.push({
      id: 'ampliacion',
      variante: 'ampliacion',
      titulo: 'Ya tiene paneles: es ampliación',
      explicacion: 'La propuesta suma los paneles nuevos a lo que ya tiene. En la cotización final hace falta su inversor (marca, kW y cantidad): edita el cuestionario.',
      frase: existente.notas,
      ediciones: q.ampliacion.es_ampliacion ? [] : [reemplazo('/cuestionario/ampliacion', q.ampliacion, { es_ampliacion: true, existente }, motivo)],
      costo: null,
      falta: q.ampliacion.es_ampliacion ? null : 'Después, en el cuestionario: su inversor (marca, kW y cantidad).',
      aplicada: q.ampliacion.es_ampliacion,
    });
  }

  // ── Titular (el recibo está a nombre de otra persona)
  const titular = /(?:a nombre de|titular es|esta a nombre de|recibo es de)\s+(?:su |la |el )?(?:papa|mama|suegr[oa]|herman[oa]|espos[oa]|arrendador|casero|la empresa|el dueno)?\s*([a-z][a-z]+(?:\s+[a-z][a-z]+){0,4})?/.exec(n);
  if (titular && titular[0].trim().length > 8) {
    const quien = titular[1] ? original.slice(titular.index + titular[0].length - titular[1].length, titular.index + titular[0].length).trim() : null;
    const nombre = quien && quien.length >= 2 ? quien.replace(/[.,;].*$/, '').slice(0, 120) : null;
    out.push({
      id: 'titular',
      variante: null,
      titulo: 'El recibo es de otra persona',
      explicacion: nombre ? `El titular del recibo es ${nombre}: la propuesta va a nombre del cliente y el recibo se compara con ese titular.` : 'El recibo está a nombre de otra persona: escribe su nombre en el cuestionario.',
      frase: fraseDe(original, n, titular),
      ediciones: !q.titular.es_el_cliente || !nombre ? [] : [reemplazo('/cuestionario/titular', q.titular, { es_el_cliente: false, nombre }, motivo)],
      costo: null,
      falta: nombre ? null : '¿A nombre de quién está el recibo?',
      aplicada: !q.titular.es_el_cliente,
    });
  }

  // ── Estructura (Diego, 12-oct): suelo, elevada (PTR) o carport van en el cuestionario y el precio del sistema las incluye
  //    con el margen de Diego; ya no se sugieren como costo adicional del catálogo (sería cobrarlas dos veces).
  //    UE-H5 (L3, 27-oct): suelo y carport nunca en residencial (Diego, 12-oct; el cuestionario de casa solo ofrece losa/lámina y
  //    PTR). Antes «paneles en la cochera» volvía carport una CASA y el PDF vendía «Paneles bifaciales, inversor y carport».
  for (const r of ESTRUCTURAS_EN_NOTAS) {
    if (q.tipo === 'casa' && r.tipo !== 'ptr') continue;
    const m = r.re.exec(n);
    if (!m) continue;
    const ya = estructuraDe(q) === r.tipo;
    out.push({
      id: 'estructura',
      variante: null,
      titulo: `Estructura: ${ETIQUETA_ESTRUCTURA[r.tipo]}`,
      explicacion: `Tus notas dicen que ${r.por}. Se cambia el tipo de estructura del cuestionario: el precio del sistema la incluye (sin renglón aparte).`,
      frase: fraseDe(original, n, m),
      ediciones: ya ? [] : [reemplazo('/cuestionario', q, { ...q, estructura: r.tipo }, motivo)],
      costo: null,
      falta: null,
      aplicada: ya,
    });
    break;
  }

  // ── Costos adicionales sugeridos
  for (const r of COSTOS) {
    const m = r.re.exec(n);
    if (!m) continue;
    const ya = c.costos_adicionales.some((x) => x.catalogo_id === r.catalogo_id || normal(x.concepto).startsWith(normal(r.concepto).slice(0, 12)));
    out.push({
      id: `costo:${r.catalogo_id}`,
      variante: null,
      titulo: `Costo adicional: ${r.concepto.replace(/\s*\(.*\)$/, '')}`,
      explicacion: `Tus notas dicen que ${r.por}. Agrégalo con el monto que pague el cliente (si no aplica, ignóralo).`,
      frase: fraseDe(original, n, m),
      ediciones: [],
      costo: { catalogo_id: r.catalogo_id, concepto: r.concepto },
      falta: null,
      aplicada: ya,
    });
  }
  return out;
}
